


import {
  defineComponent,
  onBeforeUnmount,
  onMounted,
  onUnmounted,
  PropType,
} from '@vue/composition-api';
import ReportModePane from '@/components/ExternalReports/Standard/components/ReportsView/ReportModePane/index.vue';
import MapMiscInfo from '@/components/ExternalReports/Standard/components/common/MapMiscInfo.vue';
import PhotoModal from '@/components/lib/PhotoModal/index.vue';
import type { StandardMtxExt } from '@/models/externalReports/standard/standardMtx';
import { StandardReportExt, StandardReportPhotoExt } from '@/models/externalReports/standard/standardReport';
import ManualDownloadLinks from '@/components/ExternalReports/Standard/components/common/ManualDownloadLinks/index.vue';
import usePaneControl from '@/components/ExternalReports/Standard/composables/usePaneControl';
import { useStandardReports } from '@/components/ExternalReports/Standard/composables/useReports';
import { useExtremeMap } from '@/components/ExternalReports/Standard/composables/useExtremeMap';
import { usePhotoModal } from '@/components/ExternalReports/Standard/composables/usePhotoModal';
import {
  REPORT_LIST_MAX_HEIGHT_RATE,
  REPORT_LIST_MIN_HEIGHT_RATE,
} from '@/components/ExternalReports/Standard/consts';
import { useRequestGuard } from '@/composables/useRequestGuard';
import { ExternalReportSettings } from '@/models/apis/externalReports/external_report_settings';
import { fetchImageAsObjectURL, requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';

export default defineComponent({
  props: {
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
  },
  setup() {
    const {
      paneState,
      onChangePhotoTypeVisibility: onChangePhotoTypeVisibilityOrig,
    } = usePaneControl({
      paneStyleLimits: {
        listMinHeightRate: REPORT_LIST_MIN_HEIGHT_RATE,
        listMaxHeightRate: REPORT_LIST_MAX_HEIGHT_RATE,
      },
    });

    const {
      extremeMapRef,
      mapViewState,
      loadExtremeMapEssentials,
      forceResizeExtremeMap,
      showStandardReportLayer,
      removeStandardReportLayer,
      showStandardReportPhotoPopup,
      showStandardMtxPopup,
      hidePopup,
    } = useExtremeMap(paneState);

    const {
      searchReports,
      selectReport,
      deselectReport,
      getSelectedReport,
      deselectSelectedReportPhotos,
    } = useStandardReports();

    const { doRequest } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });

    const {
      photoModalState,
      showPhotoModal: showPhotoModalOrig,
      hidePhotoModal,
    } = usePhotoModal();
    const showPhotoModal = async(photoType: string, photos: StandardReportPhotoExt[]) => {
      await doRequest(async() => {
        await requestPhotos(photos);
      }, '画像の取得に失敗しました。');
      showPhotoModalOrig(photoType, photos);
    };

    const redrawStandardReportLayer = (fitToExtent = false) => {
      const selectedReport = getSelectedReport();
      if (!selectedReport) return;

      showStandardReportLayer(selectedReport, fitToExtent);
    };

    const onChangePhotoTypeVisibility = (photoType: string, isVisible: boolean) => {
      onChangePhotoTypeVisibilityOrig(photoType, isVisible);
      redrawStandardReportLayer();
    };

    const onClickReport = async(selectedReport: StandardReportExt) => {
      hidePopup();
      // 選択中の報告書をクリックした場合、選択を解除する
      if (deselectReport(selectedReport.id)) {
        removeStandardReportLayer();
        return;
      }
      await doRequest(async() => {
        await selectReport(selectedReport.id);
      }, '報告書の取得に失敗しました。');
      redrawStandardReportLayer(true);
    };

    const onClickPhotoPin = async(photo: StandardReportPhotoExt) => {
      const isSelected = photo.isSelected;
      hidePopup();
      deselectSelectedReportPhotos();
      photo.isSelected = !isSelected;
      if (photo.isSelected) {
        // 画像を取得する
        if (!photo.savedImage) {
          await doRequest(async() => {
            photo.savedImage = await fetchImageAsObjectURL(photo.imagePath);
          }, '画像の取得に失敗しました。');
          if (!photo.savedImage) return;
        }

        showStandardReportPhotoPopup(photo);
      }
      redrawStandardReportLayer();
    };
    const onClickMtx = (mtx: StandardMtxExt) => {
      const isSelected = mtx.isSelected;
      deselectSelectedReportPhotos();
      hidePopup();
      mtx.isSelected = !isSelected;
      if (mtx.isSelected) {
        showStandardMtxPopup(mtx);
      }
      redrawStandardReportLayer();
    };
    const onAllDeselected = () => {
      hidePopup();
      deselectSelectedReportPhotos();
      redrawStandardReportLayer();
    };

    const paneResizeStopped = () => {
      forceResizeExtremeMap();
    };

    onBeforeUnmount(() => {
      try { hidePopup(); } catch {}
      try { removeStandardReportLayer(); } catch {}
      if (!photoModalState.photos) return;

      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    onMounted(async() => {
      await doRequest(async() => {
        await Promise.all([loadExtremeMapEssentials(), searchReports()]);
      }, '報告書の取得に失敗しました。');
    });

    onUnmounted(() => {
      revokePhotosObjectURLs(photoModalState.photos.map(p => p.savedImage));
    });

    return {
      mapViewState,
      paneState,
      photoModalState,
      // ref
      extremeMapRef,
      // methods
      onClickReport,
      onClickPhotoPin,
      onClickMtx,
      onAllDeselected,
      onChangePhotoTypeVisibility,
      paneResizeStopped,
      showPhotoModal,
      hidePhotoModal,
    };
  },
  components: {
    ReportModePane,
    PhotoModal,
    ManualDownloadLinks,
    MapMiscInfo,
  },
});
